import React, {ReactNode, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {
    Column,
    ColumnInstance,
    useColumnOrder,
    useFlexLayout,
    usePagination,
    useSortBy,
    useTable,
    useRowSelect,
    useExpanded,
    Hooks,
    Row,
} from 'react-table';

import {ReactComponent as ArrowIcon} from '../../../../assets/svg/arrow-bottom.svg';
import {ReactComponent as ExportIcon} from '../../../../assets/svg/download.svg';
import {ReactComponent as SettingsIcon} from '../../../../assets/svg/settings.svg';
import {I18N_NS} from '../../../../core/constants/names';
import {colors} from '../../../../theme';
import {GridConfig} from '../../../../types/api';
import {ColumnExtendedProps} from '../../../../types/grid2.0';
import {formatNumber} from '../../../../utils/number';
import {Checkbox} from '../../Checkbox';
import {Icon} from '../../Icon';
import {Pagination} from '../../Pagination';
import {SimplePagination} from '../../Pagination/simplePagination';

import {Spinner} from '../../Spinner';
import {TableTabs} from '../../TableTabs';
import {Settings} from '../Setting';

import {
    CellContainer,
    Footer,
    Header,
    HeaderAction,
    HeaderActionsWrapper,
    HeaderCell,
    HeaderControls,
    NoValuesContainer,
    NoValuesLabel,
    Root,
    SpinnerLayer,
    TableView,
    TotalRowsCount,
    HeaderContainer,
    ExpandedRow,
    ExpandedCell,
    ExpandToggle,
    ExpandedCellWrapper,
} from './styled';
import {Skeleton} from './table-skeleton';

export interface UserConfig {
    gridName: string;
    getConfig: (name: string) => Promise<GridConfig>;
    saveConfig: (config: GridConfig) => Promise<any>;
}
interface EmptyRowsLimit {
    rows: number;
    translateY?: string;
}

export interface TableProps {
    availablePageSizes?: number[];
    className?: string;
    highlightedRow?: (row: Row<any>) => string | boolean | null | undefined | void;
    selectable?: boolean;
    columns: Column<object>[];
    data?: object[];
    error?: string | null;
    hasFooter?: boolean;
    headerControls?: ReactNode;
    isLoading?: boolean;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
    pageCount?: number;
    hasNext?: boolean;
    pageIndex?: number;
    totalRowsCount?: number;
    hideSetting?: boolean;
    userConfig?: UserConfig;
    emptyRowsLimit?: EmptyRowsLimit;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
    onRowSelect?: (rows: Row<any>['original'][]) => void;
    onRowClick?: (row: any) => void;
    getRowId?: (row: any) => string;
    tableTabsSettings?: {
        baseUrl: string;
        links: {label: string; path: string}[];
    };
    emptyStateComponent?: JSX.Element;
    untoggleAllCheckedRows?: boolean;
    onRowsChecked?: React.Dispatch<React.SetStateAction<Record<string, any>[]>>;
    tableViewExtraSettings?: Record<string, string>;
    cellContainerSettings?: string;
    hoverableRow?: boolean;
    expandableRow?: (row: Row<any>) => ReactNode | null;
    showExpandableColumn?: boolean;
}

export const Table = ({
    className,
    highlightedRow,
    columns,
    data,
    error,
    hasFooter = true,
    headerControls,
    isLoading,
    onPageChange,
    onRowSelect,
    onRowClick,
    getRowId,
    selectable,
    pageCount: controlledPageCount,
    pageIndex: controlledPageIndex = 0,
    hasNext,
    totalRowsCount,
    hideSetting = false,
    emptyRowsLimit = {
        rows: 10,
    },
    userConfig,
    exportConfig,
    tableTabsSettings,
    emptyStateComponent,
    untoggleAllCheckedRows,
    onRowsChecked,
    tableViewExtraSettings = {},
    cellContainerSettings,
    hoverableRow = true,
    expandableRow,
    showExpandableColumn = true,
}: TableProps) => {
    const {t} = useTranslation(I18N_NS);
    const defaultColumn = useMemo(
        () => ({
            width: 230,
        }),
        [],
    );

    const renderSelectRow = (hooks: Hooks) => {
        hooks.visibleColumns.push((columns) => [
            {
                id: 'selection',
                width: 40,
                Header: ({getToggleAllRowsSelectedProps}) => (
                    <Checkbox {...getToggleAllRowsSelectedProps()} />
                ),
                Cell: ({row}) => <Checkbox {...row.getToggleRowSelectedProps()} />,
            },
            ...columns,
        ]);
    };

    const renderExpandColumn = (hooks: Hooks<any>) => {
        if (!showExpandableColumn) return;
        hooks.visibleColumns.push((columns) => [
            {
                id: 'expander',
                width: 30,
                disableSortBy: true,
                Header: () => null,
                Cell: ({row}) => (
                    <ExpandToggle {...row.getToggleRowExpandedProps()}>
                        <Icon
                            source={ArrowIcon}
                            width={12}
                            height={12}
                            style={{transform: `rotate(${row.isExpanded ? '0deg' : '-90deg'}`}}
                        />
                    </ExpandToggle>
                ),
            },
            ...columns,
        ]);
    };

    const [selectedRowIds, setSelectedRowIds] = useState<Record<string, boolean>>({});

    const {
        getTableBodyProps,
        getTableProps,
        headerGroups,
        prepareRow,
        setColumnOrder,
        setHiddenColumns,
        allColumns,
        rows,
        selectedFlatRows,
        toggleAllRowsSelected,
        state: {hiddenColumns, selectedRowIds: tableSelectedRowIds},
    } = useTable(
        {
            columns,
            data: data ?? [],
            manualSortBy: true,
            disableSortRemove: true,
            manualPagination: true,
            defaultColumn,
            pageCount: controlledPageCount,
            autoResetHiddenColumns: false,
            autoResetSelectedRows: false,
            getRowId,
        },
        useFlexLayout,
        useSortBy,
        useColumnOrder,
        ...(expandableRow ? [useExpanded, renderExpandColumn] : []),
        ...(selectable ? [useRowSelect, renderSelectRow] : []),
        usePagination,
    );

    const [isSettingsActive, setSettingsActive] = useState(false);
    const pinnedColumns = useMemo(
        () =>
            allColumns
                // @ts-ignore
                .filter((column) => column.ignoreConfig)
                .map((column) => ({
                    id: column.id,
                    position: allColumns.findIndex((item) => item.id === column.id),
                })),
        [columns],
    );

    useEffect(() => {
        if (!selectable) {
            return;
        }
        toggleAllRowsSelected(false);
    }, [untoggleAllCheckedRows]);

    // Apply config from LocalStorage on init
    useEffect(() => {
        if (userConfig) {
            userConfig.getConfig(userConfig.gridName).then((res) => {
                if (res) {
                    const columnsOrder: string[] = res.columns.map((item) => item);
                    for (const item of pinnedColumns) {
                        columnsOrder.splice(item.position, 0, item.id);
                    }
                    setColumnOrder(columnsOrder);
                    setHiddenColumns(res.hiddenColumns);
                }
            });
        }
    }, []);

    useEffect(() => {
        setSelectedRowIds(tableSelectedRowIds);

        if (selectedFlatRows && onRowsChecked) {
            const mapperRows = selectedFlatRows.map((i) => i.values);
            onRowsChecked(() => [...mapperRows]);
        }
    }, [tableSelectedRowIds]);

    useEffect(() => {
        if (!onRowSelect || !getRowId) return;
        const selectedRows = data?.filter((r: any) => selectedRowIds[getRowId(r)]);
        onRowSelect(selectedRows ?? []);
    }, [selectedRowIds]);

    if (error) {
        return <div>{error}</div>;
    }

    if (!data) {
        return <Skeleton />;
    }

    return (
        <Root className={className}>
            {isLoading && (
                <SpinnerLayer>
                    <Spinner />
                </SpinnerLayer>
            )}

            {(!hideSetting || headerControls || !!exportConfig) && (
                <Header>
                    <HeaderContainer alignRight={!headerControls}>
                        <HeaderControls>{headerControls}</HeaderControls>
                        {(!hideSetting || !!exportConfig) && (
                            <HeaderActionsWrapper>
                                {!hideSetting && (
                                    <HeaderAction
                                        onClick={() => setSettingsActive(!isSettingsActive)}
                                    >
                                        <Icon
                                            source={SettingsIcon}
                                            width={16}
                                            fillColor={colors.brandPrimary}
                                        />
                                        <span>{t('components.table.View')}</span>
                                    </HeaderAction>
                                )}
                                {exportConfig && (
                                    <HeaderAction
                                        onClick={() =>
                                            !exportConfig.exportPending && exportConfig.callback()
                                        }
                                    >
                                        <Icon
                                            source={ExportIcon}
                                            width={16}
                                            fillColor={
                                                exportConfig.exportPending
                                                    ? colors.textMuted
                                                    : colors.brandPrimary
                                            }
                                        />
                                        <span>{t('components.table.Export')}</span>
                                    </HeaderAction>
                                )}
                            </HeaderActionsWrapper>
                        )}
                    </HeaderContainer>
                    {tableTabsSettings && (
                        <TableTabs
                            baseUrl={tableTabsSettings.baseUrl}
                            links={tableTabsSettings.links}
                        />
                    )}
                </Header>
            )}

            <Settings
                visible={isSettingsActive}
                onClose={() => setSettingsActive(false)}
                defaultColumns={columns.map((column) => column.accessor) as string[]}
                columns={allColumns as Array<ColumnInstance<object> & ColumnExtendedProps>}
                pinnedColumns={pinnedColumns}
                setColumnOrder={setColumnOrder}
                setHiddenColumns={setHiddenColumns}
                hiddenColumns={hiddenColumns}
                userConfig={userConfig}
            />

            <TableView tableViewExtraSettings={tableViewExtraSettings} hoverableRow={hoverableRow}>
                <div {...getTableProps()} className="table">
                    <div className="thead">
                        <>
                            {headerGroups.map((headerGroup, index) => {
                                const isGroupHeader = index !== headerGroups.length - 1;
                                const className = isGroupHeader ? 'tr groupHeader' : 'tr';
                                return (
                                    <div
                                        {...headerGroup.getHeaderGroupProps()}
                                        key={`headerGroup-${index}`}
                                        className={className}
                                    >
                                        {headerGroup.headers.map((column, groupIndex) => (
                                            <HeaderCell
                                                {...column.getHeaderProps()}
                                                className="th"
                                                key={`header-${column.id}-${groupIndex}`}
                                            >
                                                {column.render('Header')}
                                            </HeaderCell>
                                        ))}
                                    </div>
                                );
                            })}
                        </>
                    </div>
                    <div {...getTableBodyProps()} className="tbody">
                        {!rows.length && emptyStateComponent}
                        {rows.length === 0 && !emptyStateComponent ? (
                            <NoValuesContainer>
                                {new Array(emptyRowsLimit.rows).fill(0).map((_, index) => (
                                    <div key={index} className="tr">
                                        {allColumns
                                            .filter((item) => item.isVisible)
                                            .map((column) => (
                                                <div
                                                    key={column.id}
                                                    role="cell"
                                                    className="td"
                                                    style={{width: column.width}}
                                                >
                                                    <CellContainer> &nbsp;</CellContainer>
                                                </div>
                                            ))}
                                    </div>
                                ))}

                                <NoValuesLabel verticalTranslate={emptyRowsLimit.translateY}>
                                    {isLoading
                                        ? t('components.table.Loading') + '...'
                                        : t('components.table.NoData')}
                                </NoValuesLabel>
                            </NoValuesContainer>
                        ) : (
                            rows.map((row: any) => {
                                prepareRow(row);

                                return (
                                    <>
                                        <div
                                            {...row.getRowProps()}
                                            role="row"
                                            tabIndex={0}
                                            onKeyDown={(e) =>
                                                e.key === 'Enter' && onRowClick?.(row.original)
                                            }
                                            onClick={() => {
                                                if (!!expandableRow && !showExpandableColumn)
                                                    row.toggleRowExpanded();
                                                onRowClick?.(row);
                                            }}
                                            className={[
                                                'tr',
                                                selectable && 'selectable',
                                                selectable && row.isSelected && 'selected',
                                                highlightedRow?.(row) && 'highlighted',
                                                row.isExpanded && 'expanded',
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                        >
                                            {row.cells.map((cell: any, cellIndex: number) => (
                                                <div
                                                    {...cell.getCellProps()}
                                                    className="td"
                                                    key={`cell-${cell.id}-${cellIndex}`}
                                                >
                                                    <CellContainer
                                                        cellContainerSettings={
                                                            cellContainerSettings
                                                        }
                                                    >
                                                        {cell.render('Cell')}
                                                    </CellContainer>
                                                </div>
                                            ))}
                                        </div>
                                        {expandableRow && (
                                            <ExpandedRow isExpanded={row.isExpanded}>
                                                <ExpandedCellWrapper>
                                                    <ExpandedCell>
                                                        {expandableRow(row)}
                                                    </ExpandedCell>
                                                </ExpandedCellWrapper>
                                            </ExpandedRow>
                                        )}
                                    </>
                                );
                            })
                        )}
                    </div>
                </div>
            </TableView>

            {hasFooter &&
                (totalRowsCount !== undefined ||
                    controlledPageCount !== undefined ||
                    hasNext !== undefined) && (
                    <Footer>
                        <TotalRowsCount>
                            {totalRowsCount !== undefined &&
                                `${t('components.table.Total')}: ${formatNumber(totalRowsCount)}`}
                        </TotalRowsCount>
                        {controlledPageCount !== undefined ? (
                            <Pagination
                                disabled={isLoading}
                                currentPageIndex={controlledPageIndex}
                                lastPageIndex={controlledPageCount ? controlledPageCount - 1 : 0}
                                onPageIndexChange={(pageIndex) =>
                                    onPageChange && onPageChange(pageIndex)
                                }
                            />
                        ) : (
                            hasNext !== undefined && (
                                <SimplePagination
                                    disabled={isLoading}
                                    currentPageIndex={controlledPageIndex}
                                    disableNext={!hasNext || data.length === 0}
                                    onPageIndexChange={(pageIndex) =>
                                        onPageChange && onPageChange(pageIndex)
                                    }
                                />
                            )
                        )}
                    </Footer>
                )}
        </Root>
    );
};
